import React, { useEffect, useState } from "react";
import Navigation from "../../components/Navigation/Navigation";
import "./promotions.css";
import editIcon from "../../assets/edit.png";
import deleteIcon from "../../assets/delete.png";
import add from "../../assets/add_white.png";
import image from "../../assets/image.png";
import clicksIcon from "../../assets/clicks.png";
import Modal from "../../components/Modal/Modal";
import { toast } from "react-hot-toast";
import { useParams } from "react-router";
import { request } from "../../utils/axios-utils";
import DeleteModal from "../../components/DeleteModal/DeleteModal";

const urlRegex = /^https?:\/\/[^\s$.?#].[^\s]*$/;

const trimText = (text, maxAmount) => {
  if (text.length <= maxAmount) {
    return text;
  }
  return text.slice(0, maxAmount - 3) + "...";
};

const Promotions = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [activeModal, setActiveModal] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    type: "",
    id: "",
  });
  const [promotions, setPromotions] = useState([]);
  const [promotionData, setPromotionData] = useState({
    heading: "",
    paragraph: "",
    image: null,
    btnText: "",
    btnUrl: "",
    active: true,
  });
  const [editPromotionData, setEditPromotionData] = useState({});
  const [duration, setDuration] = useState("this-month");
  const [analyticsData, setAnalyticsData] = useState({
    views: [],
    clicks: [],
  });

  const [filteredViews, setFilteredViews] = useState([]);
  const [filteredClicks, setFilteredClicks] = useState([]);

  const calculateTimePeriod = (duration) => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).getTime();
    const oneDay = 24 * 60 * 60 * 1000;

    switch (duration) {
      case "this-month":
        // Start of this month
        const startOfMonth = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        ).getTime();
        return [startOfMonth, now.getTime()];
      case "last-month":
        // Start and end of last month
        const startOfLastMonth = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1
        ).getTime();
        const endOfLastMonth = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        ).getTime();
        return [startOfLastMonth, endOfLastMonth];
      case "last-6-months":
        // Start of the last 6 months
        const startOfLast6Months = new Date(
          now.getFullYear(),
          now.getMonth() - 6,
          now.getDate()
        ).getTime();
        return [startOfLast6Months, now.getTime()];
      case "this-year":
        // Start of this year
        const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();
        return [startOfYear, now.getTime()];
      case "all-time":
      default:
        return [0, now.getTime()];
    }
  };

  const createPromotion = async () => {
    setError("");
    if (
      !promotionData.heading ||
      !promotionData.paragraph ||
      !promotionData.image
    ) {
      setError("Heading, paragraph, and image are required.");
      return;
    }
    if (
      (promotionData.btnText && !promotionData.btnUrl) ||
      (!promotionData.btnText && promotionData.btnUrl)
    ) {
      setError(
        "Both Button Text and Button Link are required if one of them is provided."
      );
      return;
    }

    if (promotionData.btnUrl && !urlRegex.test(promotionData.btnUrl)) {
      setError("Button Link must be a valid URL.");
      return;
    }

    try {
      setLoading("createPromotion");

      const formData = new FormData();
      formData.append("image", promotionData.image);
      formData.append("promotionData", JSON.stringify(promotionData));
      formData.append("slug", JSON.stringify(slug));

      const response = await request({
        url: "/member/create-promotion",
        method: "POST",
        data: formData,
      });

      setPromotions((prevPromotions) => {
        const updatedPrevPromotions = prevPromotions.map((promotion) => ({
          ...promotion,
          active: false,
        }));

        return [...updatedPrevPromotions, response.data.promotion];
      });

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setError("");
      setLoading("");
      setActiveModal("");
      setPromotionData({
        heading: "",
        paragraph: "",
        image: null,
        btnText: "",
        btnUrl: "",
        active: true,
      });
    }
  };

  const editPromotion = async () => {
    setError("");
    if (
      !editPromotionData.heading ||
      !editPromotionData.paragraph ||
      !editPromotionData.image
    ) {
      setError("Heading, paragraph, and image are required.");
      return;
    }
    if (
      (editPromotionData.btnText && !editPromotionData.btnUrl) ||
      (!editPromotionData.btnText && editPromotionData.btnUrl)
    ) {
      setError(
        "Both Button Text and Button Link are required if one of them is provided."
      );
      return;
    }

    if (editPromotionData.btnUrl && !urlRegex.test(editPromotionData.btnUrl)) {
      setError("Button Link must be a valid URL.");
      return;
    }

    try {
      setLoading("editPromotion");

      const formData = new FormData();
      formData.append("image", editPromotionData.newImage);
      formData.append("editPromotionData", JSON.stringify(editPromotionData));
      formData.append("slug", JSON.stringify(slug));

      const response = await request({
        url: "/member/edit-promotion",
        method: "POST",
        data: formData,
      });

      setPromotions((prevData) =>
        prevData.map((promotion) =>
          promotion.id === response.data.promotion.id
            ? response.data.promotion
            : promotion
        )
      );

      setActiveModal("");
      setEditPromotionData({});

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setError("");
      setLoading("");
    }
  };

  const deletePromotion = async () => {
    try {
      setLoading("deletePromotion");

      const response = await request({
        url: "/member/delete-promotion",
        method: "POST",
        data: { promotionId: deleteModal.id, slug },
      });

      setPromotions((prevData) =>
        prevData.filter((promotion) => promotion.id !== deleteModal.id)
      );

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  const changePromotionStatus = async (promotionId) => {
    try {
      const response = await request({
        url: "/member/change-promotion-status",
        method: "POST",
        data: { promotionId: promotionId, slug },
      });

      setPromotions(response.data.promotions);

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading("data");
        const response = await request({
          url: "/member/get-promotions",
          method: "POST",
          data: { slug },
        });

        setPromotions(response.data.promotions);
      } catch (error) {
        toast.error(error.response.data.message);
      } finally {
        setLoading("");
      }
    };

    getData();
  }, []);

  useEffect(() => {
    const [startTime, endTime] = calculateTimePeriod(duration);

    const filteredViews = analyticsData.views.filter(
      (view) => view.timestamp >= startTime && view.timestamp <= endTime
    );
    const filteredClicks = analyticsData.clicks.filter(
      (click) => click.timestamp >= startTime && click.timestamp <= endTime
    );

    setFilteredViews(filteredViews);
    setFilteredClicks(filteredClicks);
  }, [duration, analyticsData]);

  return (
    <Navigation isLoading={loading === "data"}>
      {activeModal === "createPromotion" && (
        <Modal
          heading={"Create promotion"}
          paragraph={"Enter information about your promotion."}
          closeModal={() => {
            setActiveModal("");
            setPromotionData({
              heading: "",
              paragraph: "",
              image: null,
              btnText: "",
              btnUrl: "",
              active: true,
            });
            setError("");
          }}
          btnText={"Create"}
          isLoading={loading === "createPromotion"}
          btnFuntion={loading === "createPromotion" ? null : createPromotion}
        >
          <div className="modal-label-div">
            <label htmlFor="promotionHeading" className="modal-label">
              Promotion Heading *
            </label>
          </div>
          <input
            id="promotionHeading"
            value={promotionData.heading}
            onChange={(e) => {
              setPromotionData((prevData) => ({
                ...prevData,
                heading: e.target.value,
              }));
            }}
            name="heading"
            placeholder="Promotion heading"
            className="classic-input mb-16"
            maxLength={30}
          />

          <div className="modal-label-div">
            <label htmlFor="promotionParagraph" className="modal-label">
              Promotion Paragraph *
            </label>
          </div>
          <textarea
            id="categoryDescription"
            value={promotionData.paragraph}
            onChange={(e) => {
              setPromotionData((prevData) => ({
                ...prevData,
                paragraph: e.target.value,
              }));
            }}
            name="paragraph"
            placeholder="Promotion paragraph"
            className="classic-text-area mb-16"
            maxLength={150}
          />

          <div className="modal-label-div">
            <label htmlFor="btnText" className="modal-label">
              Button Text
            </label>
          </div>
          <input
            id="btnText"
            value={promotionData.btnText}
            onChange={(e) => {
              setPromotionData((prevData) => ({
                ...prevData,
                btnText: e.target.value,
              }));
            }}
            name="btnText"
            placeholder="Button Text"
            className="classic-input mb-16"
            maxLength={20}
          />

          <div className="modal-label-div">
            <label htmlFor="btnUrl" className="modal-label">
              Button Link
            </label>
          </div>
          <input
            id="btnText"
            value={promotionData.btnUrl}
            onChange={(e) => {
              setPromotionData((prevData) => ({
                ...prevData,
                btnUrl: e.target.value,
              }));
            }}
            name="btnUrl"
            placeholder="Button Link"
            className="classic-input mb-16"
            maxLength={100}
          />

          <div className="modal-label-div">
            <label className="modal-label">Promotion Image *</label>
          </div>
          <div className="image-input-div">
            <div
              className={
                promotionData.image ? "image-input border-none" : "image-input"
              }
            >
              {promotionData.image ? (
                <>
                  <img
                    className="image-output"
                    src={
                      promotionData.image.url
                        ? promotionData.image.url
                        : URL.createObjectURL(promotionData.image)
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setPromotionData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setPromotionData((prevState) => ({
                          ...prevState,
                          image: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "editPromotion" && (
        <Modal
          heading={"Edit promotion"}
          paragraph={"Edit information about your promotion."}
          closeModal={() => {
            setActiveModal("");
            setEditPromotionData({});
            setError("");
          }}
          btnText={"Save"}
          isLoading={loading === "editPromotion"}
          btnFuntion={loading === "editPromotion" ? null : editPromotion}
        >
          <div className="modal-label-div">
            <label htmlFor="promotionHeading" className="modal-label">
              Promotion Heading *
            </label>
          </div>
          <input
            id="promotionHeading"
            value={editPromotionData.heading}
            onChange={(e) => {
              setEditPromotionData((prevData) => ({
                ...prevData,
                heading: e.target.value,
              }));
            }}
            name="heading"
            placeholder="Promotion heading"
            className="classic-input mb-16"
            maxLength={30}
          />

          <div className="modal-label-div">
            <label htmlFor="promotionParagraph" className="modal-label">
              Promotion Paragraph *
            </label>
          </div>
          <textarea
            id="categoryDescription"
            value={editPromotionData.paragraph}
            onChange={(e) => {
              setEditPromotionData((prevData) => ({
                ...prevData,
                paragraph: e.target.value,
              }));
            }}
            name="paragraph"
            placeholder="Promotion paragraph"
            className="classic-text-area mb-16"
            maxLength={150}
          />

          <div className="modal-label-div">
            <label htmlFor="btnText" className="modal-label">
              Button Text
            </label>
          </div>
          <input
            id="btnText"
            value={editPromotionData.btnText}
            onChange={(e) => {
              setEditPromotionData((prevData) => ({
                ...prevData,
                btnText: e.target.value,
              }));
            }}
            name="btnText"
            placeholder="Button Text"
            className="classic-input mb-16"
            maxLength={20}
          />

          <div className="modal-label-div">
            <label htmlFor="btnUrl" className="modal-label">
              Button Link
            </label>
          </div>
          <input
            id="btnText"
            value={editPromotionData.btnUrl}
            onChange={(e) => {
              setEditPromotionData((prevData) => ({
                ...prevData,
                btnUrl: e.target.value,
              }));
            }}
            name="btnUrl"
            placeholder="Button Link"
            className="classic-input mb-16"
            maxLength={100}
          />

          <div className="modal-label-div">
            <label className="modal-label">Promotion Image *</label>
          </div>
          <div className="image-input-div">
            <div
              className={
                !editPromotionData?.image && !editPromotionData.newImage
                  ? "image-input"
                  : "image-input border-none"
              }
            >
              {editPromotionData.image || editPromotionData.newImage ? (
                <>
                  <img
                    className="image-output"
                    src={
                      editPromotionData.newImage
                        ? URL.createObjectURL(editPromotionData.newImage)
                        : editPromotionData.image.url
                    }
                  />
                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditPromotionData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              ) : (
                <>
                  <img className="image-placeholder" src={image} />

                  <input
                    onChange={(event) => {
                      setError("");
                      const file = event.target.files[0];
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditPromotionData((prevState) => ({
                          ...prevState,
                          newImage: file,
                        }));
                      };
                      reader.readAsDataURL(file);
                    }}
                    type="file"
                    accept="image/*"
                  />
                </>
              )}
            </div>
          </div>

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "promotionAnalytics" && (
        <div
          onClick={() => {
            setActiveModal("");
            setDuration("this-month");
          }}
          className="modal-overlay"
        >
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="modal-content animate__animated animate__zoomIn animate__faster"
          >
            <div className="promotion-analytics-heading-div">
              <h1 className="promotion-analytics-heading">Analytics</h1>

              <select
                value={duration}
                className="promotion-duration-input"
                onChange={(e) => {
                  setDuration(e.target.value);
                }}
              >
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="last-6-months">Last 6 Months</option>
                <option value="this-year">This Year</option>
                <option value="all-time">All Time</option>
              </select>
            </div>

            <div className="promotion-analytics-div">
              <div className="promotion-analytic-div">
                <div className="promotion-analytic-num-div">
                  <p>{filteredViews.length}</p>
                </div>

                <p className="promotion-analytic-p">Views</p>
              </div>

              <div className="promotion-analytic-div">
                <div className="promotion-analytic-num-div">
                  <p>{filteredClicks.length}</p>
                </div>

                <p className="promotion-analytic-p">Clicks</p>
              </div>
            </div>

            <div
              onClick={() => {
                setActiveModal("");
                setDuration("this-week");
              }}
              className="promotion-analytics-close"
            >
              Close
            </div>
          </div>
        </div>
      )}

      {deleteModal.type === "deletePromotion" && (
        <DeleteModal
          heading={"Delete promotion?"}
          paragraph={"Are you sure you want to delete this promotion?"}
          deleteFunction={
            loading === "deletePromotion" ? null : deletePromotion
          }
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        ></DeleteModal>
      )}

      <h1 className="page-content-h">Promotions</h1>
      <p className="page-content-p">
        Create and manage promotions for your menu.
      </p>

      <div className="languages-container">
        {promotions?.map((promotion) => {
          return (
            <div key={promotion.id} className="promotion-bar-div">
              <div className="checkbox-container">
                <input
                  checked={promotion.active}
                  onChange={() => {
                    changePromotionStatus(promotion.id);
                  }}
                  className="checkbox-input"
                  type="checkbox"
                  id={`customCheckbox-${promotion.id}`}
                />
                <label
                  htmlFor={`customCheckbox-${promotion.id}`}
                  className="custom-checkbox"
                ></label>
              </div>

              <div className="promotion-bar-text-div">
                <h3 className="promotion-bar-h">
                  {trimText(promotion.heading, 18)}
                </h3>
                <p className="promotion-bar-p">
                  {trimText(promotion.paragraph, 50)}
                </p>
              </div>

              <div className="promotion-bar-btns-div">
                <img
                  onClick={() => {
                    setActiveModal("editPromotion");
                    setEditPromotionData(promotion);
                  }}
                  className="promotion-bar-btn-img mr-4"
                  src={editIcon}
                />
                <img
                  onClick={() => {
                    setActiveModal("promotionAnalytics");
                    setAnalyticsData({
                      views: promotion?.views,
                      clicks: promotion?.clicks,
                    });
                  }}
                  className="promotion-bar-btn-img mr-4"
                  src={clicksIcon}
                />
                <img
                  onClick={() => {
                    setDeleteModal({
                      type: "deletePromotion",
                      id: promotion.id,
                    });
                  }}
                  className="promotion-bar-btn-img "
                  src={deleteIcon}
                />
              </div>
            </div>
          );
        })}

        <div
          onClick={() => {
            setActiveModal("createPromotion");
          }}
          className="promotion-add-div"
        >
          <img className="promotion-add-img" src={add} />
          <p className="promotion-add-p">Create promotion</p>
        </div>
      </div>
    </Navigation>
  );
};

export default Promotions;
